import type { Item } from './schema.js';

/**
 * Unique item labels (OIL-68 / OIL-80): two objects never share a label – duplicates are numbered
 * "SM58 (1)", "SM58 (2)", … Empty labels are exempt.
 */

const NUMBERED = /^(.*\S)\s+\((\d+)\)$/;

/** "SM58 (2)" → { base: "SM58", n: 2 }; an unnumbered label has `n` null. */
export function splitLabelNumber(label: string): { base: string; n: number | null } {
  const m = NUMBERED.exec(label.trim());
  return m ? { base: m[1]!, n: Number(m[2]) } : { base: label.trim(), n: null };
}

export interface LabelAssignment {
  /** The label the written item gets. */
  label: string;
  /** Other items renamed so that the group is numbered throughout (plain "SM58" → "SM58 (1)"). */
  renames: { id: string; label: string }[];
}

/**
 * Label for an item written with `label` next to `others`: unchanged while no other item uses
 * it; otherwise the next number of its group. An unnumbered other item with the same base is
 * numbered too, so the group reads (1), (2), … Others are taken in id order (creation order).
 */
export function assignUniqueLabel(
  label: string,
  others: readonly Pick<Item, 'id' | 'label'>[],
): LabelAssignment {
  const trimmed = label.trim();
  if (trimmed === '') return { label, renames: [] };
  const { base } = splitLabelNumber(trimmed);
  const group = others
    .filter((o) => o.label.trim() !== '' && splitLabelNumber(o.label).base === base)
    .sort((a, b) => (a.id < b.id ? -1 : 1) /* ids are unique */);
  const clash = group.some((o) => o.label.trim() === trimmed);
  // a unique numbered label stays; a plain label stays while nobody else is in its group
  if (!clash && (group.length === 0 || splitLabelNumber(trimmed).n !== null))
    return { label, renames: [] };
  let next = Math.max(0, ...group.map((o) => splitLabelNumber(o.label).n ?? 0)) + 1;
  const renames: { id: string; label: string }[] = [];
  for (const o of group)
    if (splitLabelNumber(o.label).n === null)
      renames.push({ id: o.id, label: `${base} (${next++})` });
  return { label: `${base} (${next})`, renames };
}

/**
 * All items with unique labels (migration of existing boards, file import): every group of
 * duplicates is numbered in id order; items already numbered uniquely keep their number.
 */
export function uniqueLabels(items: readonly Item[]): Item[] {
  const sorted = [...items].sort((a, b) => (a.id < b.id ? -1 : 1) /* ids are unique */);
  const done: Item[] = [];
  const byId = new Map<string, Item>();
  for (const item of sorted) {
    const a = assignUniqueLabel(item.label, done);
    for (const r of a.renames) {
      const prev = byId.get(r.id)!;
      const renamed = { ...prev, label: r.label };
      byId.set(r.id, renamed);
      done[done.indexOf(prev)] = renamed;
    }
    const next = a.label === item.label ? item : { ...item, label: a.label };
    byId.set(item.id, next);
    done.push(next);
  }
  return items.map((i) => byId.get(i.id)!);
}
